import { router } from '@/Router';
import { useAppStore } from '@/inertia/AppStore';
import { onSelectPlayerNode } from '@/inertia/Shared/Catalog/catalog.utils';
import type { LocationChangeEventProps } from '../window.util';
import type { CustomCitationEvent, CustomCitationPayload } from './customElements/citation';
import type { OnResponseReceived } from './event.types';
import type { DFMessenger, DFMessengerChatBubble } from './messenger.types';
import type { QueryParameters } from './query-params.types';

class Chatbot {
  private df: DFMessenger | null = null;
  private dfBubble: DFMessengerChatBubble | null = null;

  private store: ReturnType<typeof useAppStore.getState> | null = null;

  private searchFilterSpec: NonNullable<NonNullable<QueryParameters['searchConfig']>['filterSpecs']>[number] = {
    dataStores: [
      `projects/zngly-corporate/locations/global/collections/default_collection/dataStores/${window.znRuntime?.dfDsId}`,
    ],
    filter: `post_status: ANY("publish")`,
  };

  private queryParams: QueryParameters = {
    searchConfig: {
      filterSpecs: [this.searchFilterSpec],
      boostSpecs: [],
    },
  };

  constructor() {
    this.enabledDialogflowCxMessenger();

    window.addEventListener('df-messenger-loaded', () => {
      this.addEventListeners();
    });
  }

  private enabledDialogflowCxMessenger() {
    const script = document.getElementById('dialogflow-cx-messenger-script') as HTMLScriptElement;
    if (!!script?.getAttribute?.('data-src')) {
      script.type = 'text/javascript';
      const src = script.getAttribute('data-src');
      script.defer = true;
      script.async = true;
      script.setAttribute('src', src || '');
    }
  }

  private addEventListeners() {
    this.df = document.querySelector('df-messenger') as DFMessenger;
    this.dfBubble = document.querySelector('df-messenger-chat-bubble') as DFMessengerChatBubble;

    // this.updateSearchFilterSpec();

    // The startNewSession() method triggers a new session, which fires the "widget.load" intent, causing the greeting to appear twice.
    // this.df?.startNewSession({ retainHistory: true });
    this.df?.setQueryParameters(this.queryParams);

    // Auto-open the chatbot after a short delay
    setTimeout(() => {
      this.dfBubble?.openChat();
    }, 1000); // 1 second delay

    window.addEventListener('df-response-received', (event) => {
      this.onResponseReceived(event as OnResponseReceived);
    });
    window.addEventListener('df-custom-citation-clicked', (event) => {
      this.onDfCustomCitationClicked(event as CustomCitationEvent);
    });

    // location change event
    const onLocationChange = (e: CustomEvent<LocationChangeEventProps>) => {
      this.store = useAppStore.getState();
    };
    window.addEventListener('locationchange', onLocationChange);

    // remove event listener on unmount
    window.addEventListener('beforeunload', () => {
      window.removeEventListener('locationchange', onLocationChange);
    });
  }

  private onDfCustomCitationClicked(event: CustomCitationEvent) {
    if (!event?.detail?.citation?.anchor?.href) {
      // console.log('No URL found in the citation.', event);
      return;
    }

    try {
      const url = new URL(event.detail.citation.anchor.href);

      const { origin, pathname, searchParams } = url;

      if (origin !== window.location.origin) {
        // Open external links in a new tab
        // console.log('External link clicked:', event.detail.citation.anchor.href);
        return;
      }

      // Handle internal links
      if (searchParams.has('c')) {
        const contentId = searchParams.get('c');
        if (contentId) {
          this.navigateToContentId(contentId);
          return;
        }
      }

      // if path like /content/${id}
      const contentRegex = /\/content\/([a-zA-Z0-9-]+)/;
      if (pathname.match(contentRegex)) {
        const contentId = pathname.match(contentRegex)?.[1];
        if (contentId) {
          this.navigateToContentId(contentId);
          return;
        }
      }
    } catch (error) {
      // console.error('Invalid URL:', event.detail.citation.anchor.href);
      return;
    }
  }

  private navigateToContentId(contentId?: string | null | undefined) {
    if (!contentId || typeof contentId !== 'string' || contentId.trim() === '') {
      return;
    }

    const currentUrlCatalogId = new URL(window.location.href).searchParams.get('c');
    if (currentUrlCatalogId?.startsWith?.(contentId)) {
      return;
    }

    // usually most ids are a relay id in the base encoded format of "post:123"
    // looking like this: cG9zdDoyMQ==
    // for some reason when we get the href the base64 encoded = are missing

    this.store = useAppStore.getState();

    if (this.store?.pageType === 'hub') {
      const hub = this.store?.hub;

      let hubPageId = null as string | null | undefined;
      let catalogId = null as string | null | undefined;

      hub?.connectionHubToHps?.edges?.forEach((edge) => {
        if (!edge?.node || !!hubPageId) return;
        const hubPageNode = edge?.node as NonNullable<
          NonNullable<typeof hub>['connectionHubToHps']
        >['edges'][number]['node'];

        // loop through the hub pages and find the catalog id
        hubPageNode.hubPage?.node?.connectionHpToCs?.edges?.forEach((edge) => {
          if (!edge?.node || !!catalogId) return;
          const catalogNode = edge?.node as NonNullable<
            NonNullable<typeof hubPageNode.hubPage.node>['connectionHpToCs']
          >['edges'][number]['node'];

          if (catalogNode?.catalogId?.startsWith(contentId)) {
            catalogId = catalogNode?.catalogId;
            hubPageId = hubPageNode?.pageId;

            return;
          }
        });
      });

      if (catalogId && hubPageId) {
        router.navigate({
          // @ts-ignore
          search: (prev) => {
            return {
              ...prev,
              c: catalogId,
              nav: hubPageId,
            };
          },
        });

        return;
      }
    }

    const foundNode = this.store.nodes.find((node) => {
      return node?.id?.startsWith(contentId);
    });

    if (!foundNode) {
      // maybe navigate to the single page, add some form of args for this to be an option
      router.navigate({
        to: '/content/$postId',
        params: {
          postId: contentId,
        },
        search: {
          c: contentId,
        },
      });

      return;
    }

    onSelectPlayerNode(foundNode, 0);
  }

  private onResponseReceived(event: OnResponseReceived) {
    // console.log('Response received:', event);

    event.preventDefault();

    if (!event.detail?.messages?.length) {
      // console.log('No messages found in the response.', event);
      return;
    }

    const texts = event.detail.messages.filter((message) => {
      return message.type === 'text';
    });

    if (texts.length > 0) {
      for (const text of texts) {
        this.df?.renderCustomText(text.text || '', true);
      }
    }

    const citationObjects = event.detail.messages.filter((message) => {
      return message.type === 'match_citations';
    });

    if (citationObjects.length > 0) {
      this.df?.renderCustomCard(
        citationObjects.map((obj) => {
          return {
            type: 'custom_template',
            name: 'df-custom-citation',
            payload: JSON.stringify({
              citations: (obj.citations || []).slice(0, 10).map((c) => {
                return {
                  title: c.title,
                  subtitle: c.subtitle,
                  anchor: {
                    href: c.actionLink,
                  },
                };
              }),
            } as CustomCitationPayload),
          };
        })
      );
    }
  }

  private updateSearchFilterSpec() {
    this.store = useAppStore.getState();

    const pageType = this.store?.pageType;

    if (
      ['single', 'search', 'front_page', '404', 'view-all', 'privacy-policy', 'unknown'].includes(pageType ?? 'unknown')
    ) {
      // default query params
      this.searchFilterSpec.filter = `post_status: ANY("publish")`;
    }
  }

  public static generateSearchFilterSpec() {
    //
  }
}

new Chatbot();

export default {};
